/**
 * Chart Share Service
 * Business logic for share configuration and validation
 */

import { devError } from '../utils/devLog';
import { ShareConfig } from '../types/share.types';

/**
 * Service for managing chart share configurations
 */
export class ChartShareService {
  /**
   * Create default share configuration
   * By default: Name and Birth Time visible, Place, Location (coordinates), and Timezone hidden
   */
  getDefaultShareConfig(): ShareConfig {
    return {
      fields: {
        name: true,
        birthTime: true,
        birthPlace: false,  // Hidden by default
        coordinates: false, // Hidden by default
        timezone: false,    // Hidden by default
      },
      expires_in_days: null,  // Never expires by default
    };
  }

  /**
   * Validate share configuration
   *
   * @param config - Share configuration to validate
   * @returns true if valid, false otherwise
   */
  validateShareConfig(config: ShareConfig): boolean {
    // Validate expires_in_days range if provided
    if (config.expires_in_days !== undefined && config.expires_in_days !== null) {
      if (config.expires_in_days < 1 || config.expires_in_days > 365) {
        devError('[ChartShareService] expires_in_days must be between 1 and 365 days');
        return false;
      }
    }

    return true;
  }

  /**
   * Merge custom config with defaults
   *
   * @param customConfig - Partial configuration from user
   * @returns Complete merged configuration
   */
  mergeWithDefaults(customConfig?: Partial<ShareConfig>): ShareConfig {
    const defaults = this.getDefaultShareConfig();

    if (!customConfig) {
      return defaults;
    }

    return {
      fields: {
        ...defaults.fields,
        ...(customConfig.fields || {}),
      },
      expires_in_days: customConfig.expires_in_days !== undefined
        ? customConfig.expires_in_days
        : defaults.expires_in_days,
    };
  }

  /**
   * Generate share URL from short code
   *
   * @param shortCode - Short code for the chart
   * @returns Full URL for sharing
   */
  generateShareUrl(shortCode: string): string {
    return `https://bhriguhora.com/s/${shortCode}`;
  }

  /**
   * Get list of visible section keys
   * Note: All sections are always visible now
   *
   * @returns Array of all section keys
   */
  getVisibleSections(): string[] {
    return ['planet_positions', 'panchanga', 'detailed_panchanga', 'astronomical_data'];
  }

  /**
   * Filter metadata for preview (client-side filtering)
   * Useful for showing preview before creating share link
   *
   * @param metadata - Full chart metadata
   * @param config - Share configuration
   * @returns Filtered metadata object
   */
  filterMetadata(metadata: Record<string, any>, config: ShareConfig): Record<string, any> {
    const filtered: Record<string, any> = {};

    if (config.fields.name && metadata.name) {
      filtered.name = metadata.name;
    }

    if (config.fields.birthTime && metadata.birthTime) {
      filtered.birthTime = metadata.birthTime;
    }

    if (config.fields.birthPlace && metadata.birthPlace) {
      filtered.birthPlace = metadata.birthPlace;
    }

    if (config.fields.coordinates && metadata.coordinates) {
      filtered.coordinates = metadata.coordinates;
    }

    if (config.fields.timezone && metadata.timezone) {
      filtered.timezone = metadata.timezone;
    }

    return filtered;
  }

  /**
   * Format expiry date for display
   *
   * @param expiresAt - ISO date string or null
   * @returns Formatted expiry message
   */
  formatExpiryMessage(expiresAt: string | null | undefined): string {
    if (!expiresAt) {
      return 'Never expires';
    }

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry <= 0) {
      return 'Expired';
    } else if (daysUntilExpiry === 1) {
      return 'Expires in 1 day';
    } else if (daysUntilExpiry <= 30) {
      return `Expires in ${daysUntilExpiry} days`;
    } else {
      return `Expires on ${expiryDate.toLocaleDateString()}`;
    }
  }
}

// Export singleton instance
export const chartShareService = new ChartShareService();
